import React from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Slider, Stack, TextField, Tooltip, Typography } from '@mui/material';
import type { Anchor, Position } from '../../../utils/editing/editList';
import { ANCHORS } from '../../../utils/editing/editList';
import { ANCHOR_LABELS } from '../editStudioModel';

/** Small controls the studio's panels share. */

interface SecondsFieldProps {
  label: string;
  valueMs: number | null;
  /** Null allowed means "from the start" / "to the end". */
  allowEmpty?: boolean;
  maxMs: number;
  onChange: (ms: number | null) => void;
  testId?: string;
}

/** A time in seconds, stored as milliseconds. */
export const SecondsField: React.FC<SecondsFieldProps> = ({ label, valueMs, allowEmpty = false, maxMs, onChange, testId }) => {
  const [text, setText] = React.useState(valueMs === null ? '' : String(Math.round(valueMs) / 1000));
  React.useEffect(() => {
    setText(valueMs === null ? '' : String(Math.round(valueMs) / 1000));
  }, [valueMs]);
  const commit = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      if (allowEmpty) onChange(null);
      else setText(valueMs === null ? '' : String(Math.round(valueMs) / 1000));
      return;
    }
    const seconds = Number(trimmed.replace(',', '.'));
    if (!Number.isFinite(seconds) || seconds < 0) {
      setText(valueMs === null ? '' : String(Math.round(valueMs) / 1000));
      return;
    }
    onChange(Math.min(maxMs, Math.round(seconds * 1000)));
  };
  return (
    <TextField
      size="small"
      label={label}
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => { if (event.key === 'Enter') (event.target as HTMLInputElement).blur(); }}
      inputProps={{ inputMode: 'decimal', 'data-testid': testId, 'aria-label': label }}
      placeholder={allowEmpty ? (label.toLowerCase().indexOf('from') >= 0 ? 'start' : 'end') : undefined}
      sx={{ width: 110 }}
      InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">s</Typography> }}
    />
  );
};

interface PositionPickerProps {
  value: Position;
  onChange: (position: Position) => void;
  /** Hide the margin (titles sit in the middle). */
  withMargin?: boolean;
}

/** A 3×3 grid of anchors plus the margin from the edge. */
export const PositionPicker: React.FC<PositionPickerProps> = ({ value, onChange, withMargin = true }) => (
  <Stack spacing={1}>
    <Typography variant="caption" color="text.secondary">Where</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 34px)', gap: 0.5 }} role="radiogroup" aria-label="Where on the picture">
      {ANCHORS.map((anchor: Anchor) => {
        const selected = value.anchor === anchor && value.xPct === null;
        return (
          <Tooltip key={anchor} title={ANCHOR_LABELS[anchor]}>
            <Box
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={ANCHOR_LABELS[anchor]}
              onClick={() => onChange({ ...value, anchor, xPct: null, yPct: null })}
              sx={{
                width: 34,
                height: 24,
                borderRadius: 1,
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: selected ? 'primary.main' : 'background.paper',
                cursor: 'pointer',
                p: 0,
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
    {withMargin ? (
      <Box sx={{ px: 0.5 }}>
        <Typography variant="caption" color="text.secondary">Distance from the edge</Typography>
        <Slider size="small" min={0} max={20} step={0.5} value={value.marginPct} onChange={(_, v) => onChange({ ...value, marginPct: v as number })} aria-label="Distance from the edge" valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`} />
      </Box>
    ) : null}
    {value.xPct !== null ? (
      <Button size="small" onClick={() => onChange({ ...value, xPct: null, yPct: null })}>Snap back to a corner</Button>
    ) : null}
  </Stack>
);

interface ChoiceProps<T extends string> {
  label: string;
  value: T;
  options: Array<{ value: T; label: string; disabled?: boolean }>;
  onChange: (value: T) => void;
  testId?: string;
}

export function Choice<T extends string>({ label, value, options, onChange, testId }: ChoiceProps<T>) {
  const id = `choice-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <FormControl size="small" fullWidth>
      <InputLabel id={id}>{label}</InputLabel>
      <Select labelId={id} label={label} value={value} onChange={(event) => onChange(event.target.value as T)} inputProps={{ 'data-testid': testId }}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value} disabled={option.disabled}>{option.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

interface DbSliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}

export const DbSlider: React.FC<DbSliderProps> = ({ label, value, min, max, onChange }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}: {value > 0 ? '+' : ''}{value} dB</Typography>
    <Slider size="small" min={min} max={max} step={1} value={value} onChange={(_, v) => onChange(v as number)} aria-label={label} />
  </Box>
);

export const PanelHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{children}</Typography>
);
